/**
 * Case normalization for IDENTITY FIELDS pre-filled from a label reading
 * (operator finding, console-prefill item 1).
 *
 * Vision transcribes verbatim — the artifact is correct and untouched, and
 * "The machine's reading" keeps showing exactly what the label printed.
 * But what lands in an EDITABLE IDENTITY FIELD is a display form, and the
 * naming standard wants title case with accents preserved:
 *
 *   "GRANDE CUVÉE 173ÈME ÉDITION" → "Grande Cuvée 173ème Édition"
 *
 * THE RULE, stated:
 *
 * 1. MIXED-case input is INTENTIONAL and is returned untouched. Somebody
 *    (a human or a careful label) already decided "d'Yquem", "McLaren",
 *    "LVMH Estates" — we never second-guess casing that carries signal.
 *    "Mixed" = the letters contain both cases.
 *
 * 2. SINGLE-case input (ALL CAPS or all lower) carries no casing signal,
 *    so it is rebuilt as title case, word by word (hyphens and apostrophes
 *    both bound words: SAINT-JULIEN → Saint-Julien, D'YQUEM → d'Yquem):
 *    a. a word starting with a DIGIT keeps its letters lowercase — the
 *       ordinal rule ("173ÈME" → "173ème");
 *    b. a VOWELLESS word of 2+ letters is treated as an acronym and kept
 *       uppercase ("LVMH" → "LVMH", "DRC" → "DRC") — the one heuristic
 *       that can tell an acronym from a shouted word without a dictionary;
 *    c. a leading elision particle before an apostrophe is lowercased and
 *       the next letter capitalized ("D'YQUEM" → "d'Yquem", "L'ÉGLISE" →
 *       "l'Église");
 *    d. connecting particles are lowercased when not the first word
 *       ("MARQUÉS DE RISCAL" → "Marqués de Riscal");
 *    e. everything else: first letter up, rest down, accents riding along
 *       (JS case mapping is Unicode-aware: É↔é).
 *
 * KNOWN EDGE CASES of the rule, accepted and stated:
 *  - an all-caps acronym WITH vowels ("FIAT" shapes) title-cases to
 *    "Fiat" — indistinguishable from a shouted word without a dictionary;
 *  - a vowelless real word in caps (rare in wine naming) stays uppercase;
 *  - mixed-case typos ("EnVINate") are preserved as intentional — rule 1
 *    cannot tell a typo from a brand.
 */

const CONNECTING_PARTICLES = new Set([
  'de', 'du', 'des', 'la', 'le', 'les', 'di', 'da', 'del', 'della', 'dei',
  'e', 'i', 'y', 'of', 'the', 'van', 'von', 'zu', 'im', 'am', 'a', 'al',
]);

const VOWELS = /[aeiouyàáâãäåæèéêëìíîïòóôõöøùúûüýÿœ]/i;

const hasUpper = (s: string) => s !== s.toLowerCase() && /\p{L}/u.test(s);
const hasLower = (s: string) => s !== s.toUpperCase() && /\p{L}/u.test(s);

/** One word-part (already split on space/hyphen/apostrophe boundaries). */
function casePart(part: string, isFirstWord: boolean): string {
  if (!part) return part;
  if (/^\d/.test(part)) return part.toLowerCase();                    // 2a
  if (part.length >= 2 && !VOWELS.test(part)) return part.toUpperCase(); // 2b
  const lower = part.toLowerCase();
  if (!isFirstWord && CONNECTING_PARTICLES.has(lower)) return lower;  // 2d
  return lower.charAt(0).toUpperCase() + lower.slice(1);              // 2e
}

export function normalizeDisplayCase(input: string): string {
  const text = input.trim().replace(/\s+/g, ' ');
  if (!text) return text;
  if (hasUpper(text) && hasLower(text)) return text;                  // rule 1

  return text.split(' ').map((word, wi) => {
    // Elision particle: d'..., l'... (rule 2c) — the remainder is cased as
    // its own word-start, hyphen-aware.
    const elision = word.match(/^([dl])'(.+)$/i);
    if (elision) {
      const rest = elision[2].split('-').map((p) => casePart(p, true)).join('-');
      return `${elision[1].toLowerCase()}'${rest}`;
    }
    // Hyphenated words: each side cased as a word start (SAINT-JULIEN →
    // Saint-Julien); the particle rule still applies inside.
    return word.split('-').map((p, pi) => casePart(p, wi === 0 && pi === 0)).join('-');
  }).join(' ');
}

/** Accent-insensitive fold for matching a read against picker options. */
export function foldForMatch(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
